export interface AvailableDateItem {
  date: string
  available: boolean
}

export interface DateValidationOptions {
  availableDates?: AvailableDateItem[]
  startDate?: Date
  endDate?: Date
  originalStartDate?: Date
  originalEndDate?: Date
  heroDuration?: number
  isEdit?: boolean
  isStartDateInPast?: boolean
}

export const createDateValidator = (options: DateValidationOptions) => {
  const {
    availableDates,
    startDate,
    endDate,
    heroDuration,
    isEdit,
    isStartDateInPast,
  } = options

  const normalizeLocalDate = (d: Date): Date =>
    new Date(d.getFullYear(), d.getMonth(), d.getDate())

  const formatDateLocal = (date: Date): string => {
    const nd = normalizeLocalDate(date)
    const year = nd.getFullYear()
    const month = String(nd.getMonth() + 1).padStart(2, '0')
    const day = String(nd.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
  }

  const dateAvailabilityMap = new Map<string, boolean>()
  if (availableDates?.length) {
    availableDates.forEach(item => {
      const apiDateString = item.date.includes('T')
        ? item.date.split('T')[0]
        : item.date.split(' ')[0]
      dateAvailabilityMap.set(apiDateString, item.available)
    })
  }

  const isAvailable = (date: Date): boolean => {
    const dateString = formatDateLocal(date)
    // If date is in the map and marked as unavailable (booked), it's not available
    if (dateAvailabilityMap.has(dateString)) {
      return dateAvailabilityMap.get(dateString) === true
    }
    // If date not in map, it's available (not booked)
    return true
  }

  const isBooked = (date: Date): boolean => {
    const dateString = formatDateLocal(date)
    return (
      dateAvailabilityMap.has(dateString) &&
      !dateAvailabilityMap.get(dateString)
    )
  }

  const hasBookedDateBetween = (startDate: Date, endDate: Date): boolean => {
    const start = normalizeLocalDate(startDate)
    const end = normalizeLocalDate(endDate)

    // Check each date in the range
    for (
      let date = new Date(start);
      date <= end;
      date.setDate(date.getDate() + 1)
    ) {
      if (isBooked(date)) {
        return true
      }
    }
    return false
  }

  return {
    isAvailable,
    isDisabled: (date: Date, isEndDate = false): boolean => {
      const nDate = normalizeLocalDate(date)
      const today = new Date()
      today.setHours(0, 0, 0, 0)

      // Disable past dates (unless editing and start date is in past)
      if (nDate < today) {
        if (!isEndDate && isEdit && isStartDateInPast) return false
        return true
      }

      // Disable booked dates
      if (isBooked(nDate)) return true

      // Hero duration validation
      if (isEndDate && startDate && heroDuration) {
        const nStart = normalizeLocalDate(startDate)
        const daysDiff = Math.floor(
          (nDate.getTime() - nStart.getTime()) / (1000 * 60 * 60 * 24)
        )
        if (daysDiff >= heroDuration) return true
      }

      if (!isEndDate && endDate && heroDuration) {
        const nEnd = normalizeLocalDate(endDate)
        const daysDiff = Math.floor(
          (nEnd.getTime() - nDate.getTime()) / (1000 * 60 * 60 * 24)
        )
        if (daysDiff >= heroDuration) return true
      }

      // Consecutive validation: check if there's a booked date between start and end
      if (isEndDate && startDate && !(isEdit && isStartDateInPast)) {
        const nStart = normalizeLocalDate(startDate)
        if (nDate < nStart) return true

        // Disable if there's any booked date between start and this date
        if (hasBookedDateBetween(nStart, nDate)) {
          return true
        }
      }

      if (!isEndDate && endDate) {
        const nEnd = normalizeLocalDate(endDate)
        if (nDate > nEnd) return true

        // Disable if there's any booked date between this date and end
        if (hasBookedDateBetween(nDate, nEnd)) {
          return true
        }
      }

      return false
    },
    getError: (
      date: Date | undefined,
      fieldName: string
    ): string | undefined => {
      if (!date) return undefined

      const nDate = normalizeLocalDate(date)
      const skipStartDateCheck = isEdit && isStartDateInPast
      const today = new Date()
      today.setHours(0, 0, 0, 0)

      // Check for past dates
      if (nDate < today && !skipStartDateCheck) {
        return `${fieldName === 'start' ? 'Start' : 'End'} date cannot be in the past.`
      }

      // Check if date is booked
      if (isBooked(nDate) && !skipStartDateCheck) {
        return `Selected ${fieldName} date is not available (already booked). Please choose a different date.`
      }

      // Hero duration validation
      if (fieldName === 'end' && startDate && heroDuration) {
        const nStart = normalizeLocalDate(startDate)
        const daysDiff = Math.floor(
          (nDate.getTime() - nStart.getTime()) / (1000 * 60 * 60 * 24)
        )
        if (daysDiff >= heroDuration) {
          return `End date must be within ${heroDuration} days from the start date.`
        }
      }

      if (fieldName === 'start' && endDate && heroDuration) {
        const nEnd = normalizeLocalDate(endDate)
        const daysDiff = Math.floor(
          (nEnd.getTime() - nDate.getTime()) / (1000 * 60 * 60 * 24)
        )
        if (daysDiff >= heroDuration) {
          return `Start date must be within ${heroDuration} days before the end date.`
        }
      }

      // Consecutive validation: check for booked dates in between
      if (fieldName === 'end' && startDate && !skipStartDateCheck) {
        const nStart = normalizeLocalDate(startDate)
        if (hasBookedDateBetween(nStart, nDate)) {
          return `There are booked dates between your selected dates. Please select consecutive available dates.`
        }
      }

      if (fieldName === 'start' && endDate && !skipStartDateCheck) {
        const nEnd = normalizeLocalDate(endDate)
        if (hasBookedDateBetween(nDate, nEnd)) {
          return `There are booked dates between your selected dates. Please select consecutive available dates.`
        }
      }

      return undefined
    },

    getAvailableDateRange: (): string | null => {
      if (!availableDates?.length) return 'All future dates are available'

      const bookedCount = availableDates.filter(item => !item.available).length

      if (bookedCount === 0) return 'All future dates are available'

      if (bookedCount === 1) {
        const bookedDate = availableDates.find(item => !item.available)
        if (bookedDate) {
          const apiDateString = bookedDate.date.includes('T')
            ? bookedDate.date.split('T')[0]
            : bookedDate.date.split(' ')[0]
          const [y, m, d] = apiDateString.split('-').map(Number)
          const date = normalizeLocalDate(new Date(y, m - 1, d))
          const formatDate = (date: Date) =>
            date.toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
              year: 'numeric',
            })
          return `${formatDate(date)} is booked. All other dates are available.`
        }
      }

      return `${bookedCount} dates are booked. Please select consecutive available dates.`
    },
  }
}

export const createAdDateValidator = (
  availableDates: AvailableDateItem[] | undefined,
  adStartDate: Date | undefined,
  adEndDate: Date | undefined,
  heroDuration?: number,
  isEdit: boolean = false,
  isStartDateInPast: boolean = false
) => {
  return createDateValidator({
    availableDates,
    startDate: adStartDate,
    endDate: adEndDate,
    heroDuration,
    isEdit,
    isStartDateInPast,
  })
}
